/**
 * Every word the Product feedback page shows (SA-1305). The kind, status and speaker words are the
 * panel's own, re-exported, so the product team and the sender read one vocabulary.
 */
export {
  KIND_WORDS, SPEAKER_HELP, SPEAKER_YOU, STATUS_WORDS, type ReportStatus,
} from '@/components/helpFeedback/helpFeedbackText'

export const TITLE = 'Product feedback'
export const SUBTITLE = 'What people sent from Help & feedback.'
export const TABLE_LABEL = 'Product feedback'

export const COL_REFERENCE = 'Reference'
export const COL_KIND = 'Kind'
export const COL_SUMMARY = 'Summary'
export const COL_PAGE = 'Page'
export const COL_FROM = 'From'
export const COL_SENT = 'Sent'
export const COL_STATUS = 'Status'

export const KIND_FILTER = 'Kind'
export const ALL_KINDS = 'All kinds'
export const STATUS_FILTER = 'Status'
export const ALL_STATUSES = 'All statuses'
export const FROM_FILTER = 'From'
export const ANYONE = 'Anyone'
export const CLEAR_FILTERS = 'Clear filters'
export const HAS_NOTE = 'Has a note'
export const NO_SUMMARY = 'No summary'
export const DASH = '—'

export const pageChip = (path: string) => `Page: ${path}`
export const removePageChip = (path: string) => `Remove the page filter ${path}`
export const filterToPage = (path: string) => `Show only feedback from ${path}`

export function guidanceToggle(count: number): string {
  if (count === 1) return 'Show 1 question Help answered'
  return `Show ${count} questions Help answered`
}

export const GAPS_TOGGLE = 'Only help gaps'
export const GAPS_HINT = 'Questions sent after Help could not find the answer, grouped by page.'
export const NO_PAGE = 'No page'
export const HELP_GAP = 'Help gap'
export const gapGroup = (path: string | null, count: number) =>
  `${path ?? NO_PAGE} · ${count === 1 ? '1 question' : `${count} questions`}`

export const showing = (shown: number, total: number) => `Showing ${shown} of ${total}`

export const EMPTY = 'No feedback yet.'
export const EMPTY_FILTERED = 'Nothing matches these filters.'
export const LOAD_FAILED = "Couldn't load the feedback."
export const TRY_AGAIN = 'Try again'
export const LOADING = 'Loading feedback'

// --- the detail -------------------------------------------------------------------------------------

export const DRAWER_LABEL = 'Feedback report'
export const RESIZE_LABEL = 'Resize the feedback report'
export const REPORT_GONE = 'This report is gone.'
export const REPORT_FAILED = "Couldn't load this report."
export const REPORT_LOADING = 'Loading the report'
export const CLOSE = 'Close'

export const fromLine = (name: string | null, time: string) => (name ? `from ${name} · ${time}` : time)

export const DOING_HEADING = 'What they were doing'
export const HELP_ANSWERED = 'Help answered this.'
export const CONVERSATION_HEADING = 'Conversation'
export const NO_CONVERSATION = 'They sent it without a conversation.'
export const SPOKEN = 'Spoken'
export const FOUND_WORDS: Record<'yes' | 'partly' | 'no', string> = {
  yes: 'Found in the help',
  partly: 'Partly in the help',
  no: 'Not in the help',
}
export const ANSWER_SOURCES = 'Drew on'

export function timelineHeading(errors: number): string {
  if (errors === 0) return 'Timeline'
  return errors === 1 ? 'Timeline · 1 error' : `Timeline · ${errors} errors`
}
export const NO_TIMELINE = 'Nothing was recorded before they sent it.'
export const ENVIRONMENT_HEADING = 'Environment'
export const NOT_ATTACHED_HEADING = 'Not attached'
export const OPEN_PAGE = 'Open the page'
export const OPEN_PAGE_HINT = 'Opens in a new tab, as they saw it.'

const REMOVED_WORDS: Record<string, string> = {
  page: 'the page',
  errors: 'the errors',
  actions: 'the recent steps',
  environment: 'the browser and build',
}

/** "They removed the page and the browser and build." */
export function removedLine(chips: string[]): string {
  const words = chips.map((c) => REMOVED_WORDS[c] ?? c)
  if (words.length === 0) return ''
  const list = words.length === 1 ? words[0] : `${words.slice(0, -1).join(', ')} and ${words[words.length - 1]}`
  return `They removed ${list}.`
}

// The timeline's lines.
export const opened = (where: string) => `Opened ${where}`
export const pressed = (name: string) => `Pressed ${name}`
export const changed = (field: string) => `Changed ${field}`
export const UNKNOWN_EVENT = 'Event'
export function requestLine(method: string, path: string, status: number, ms?: number): string {
  const answer = status === 0 ? 'no answer' : String(status)
  return [`${method} ${path}`, answer, ms !== undefined ? `${ms} ms` : null].filter(Boolean).join(' · ')
}

// The environment facts.
export const ENV_BROWSER = 'Browser'
export const ENV_WINDOW = 'Window'
export const ENV_BUILD = 'Build'
export const ENV_FEATURES = 'Features on'
export const ENV_VIEW_AS = 'Viewing as'
export const ENV_VIEW_AS_VALUE = 'Another person'

// --- staff actions ----------------------------------------------------------------------------------

export const STATUS_LABEL = 'Status'
export const STATUS_HINT = 'They see this status in Your feedback.'
export const NOTE_LABEL = 'Private note'
export const NOTE_HINT = 'Only the product team sees this.'
export const SAVE_NOTE = 'Save note'
export const NOTE_SAVED = 'Saved.'
export const noteBy = (name: string | null, time: string) => (name ? `${name} · ${time}` : time)
export const CHANGED_BY_SOMEONE = 'Someone changed this report. Reload it to see their change.'
export const RELOAD = 'Reload'
export const SAVE_FAILED = "Couldn't save. Try again."
